import React from 'react'
import { Element, Icon } from 'react-bulma-components'
import { Link } from 'react-router-dom'
import { ArrowLeftIcon, ArrowRightIcon } from '../icons'

type PageNavigationProps<ResourceType extends { _id: string }> = {
  items: [ResourceType | undefined, ResourceType | undefined]
  linkBuilder: (item: ResourceType) => string
  labelBuilder: (item: ResourceType) => string
  /**
   * Optional custom link renderer (e.g. to handle virtual items that must be created on click).
   * Receives the item, the default content (icon + label), and context (e.g. for data-test).
   */
  renderLink?: (
    item: ResourceType,
    content: React.ReactNode,
    context: { direction: 'previous' | 'next' },
  ) => React.ReactNode
}

const buildLinkContent = (label: string, direction: 'previous' | 'next'): React.ReactNode => (
  <Icon.Text flexWrap="nowrap">
    {direction === 'previous' && (
      <>
        <Icon>
          <ArrowLeftIcon />
        </Icon>
        &nbsp;
      </>
    )}
    {label}
    {direction === 'next' && (
      <>
        &nbsp;
        <Icon>
          <ArrowRightIcon />
        </Icon>
      </>
    )}
  </Icon.Text>
)

export const PageNavigation = <ResourceType extends { _id: string }>({
  items: [previous, next],
  linkBuilder,
  labelBuilder,
  renderLink,
}: PageNavigationProps<ResourceType>): JSX.Element => {
  const previousLabel = previous ? labelBuilder(previous) : undefined
  const nextLabel = next ? labelBuilder(next) : undefined

  const previousContent = previous ? (
    renderLink ? (
      renderLink(previous, buildLinkContent(previousLabel!, 'previous'), {
        direction: 'previous',
      })
    ) : (
      <Link to={linkBuilder(previous)} data-test="page-navigation-previous">
        {buildLinkContent(previousLabel!, 'previous')}
      </Link>
    )
  ) : null

  const nextContent = next ? (
    renderLink ? (
      renderLink(next, buildLinkContent(nextLabel!, 'next'), { direction: 'next' })
    ) : (
      <Link to={linkBuilder(next)} data-test="page-navigation-next">
        {buildLinkContent(nextLabel!, 'next')}
      </Link>
    )
  ) : null

  return (
    <Element display="flex" justifyContent="space-between">
      {previousContent ?? <Element />}
      {previousContent && nextContent && <>&bull;</>}
      {nextContent}
    </Element>
  )
}
